import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/api.js";
import { getAccessToken } from "@/auth.js";

// Decodes the JWT `exp` claim from the access token and toasts a warning when
// the session is about to expire. The "Stay signed in" action calls the
// existing refresh flow; if the user ignores it, the normal 401-retry-on-refresh
// fallback in request() handles the next API call.

const WARN_SECONDS = 120; // warn 2 minutes before expiry
const POLL_MS = 30_000; // recheck every 30s

function decodeExp(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2 || !parts[1]) return null;
    // base64url → base64
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "===".slice((b64.length + 3) % 4);
    const json = JSON.parse(atob(padded)) as { exp?: number };
    return typeof json.exp === "number" ? json.exp : null;
  } catch {
    return null;
  }
}

export function useSessionExpiry() {
  // Track the last access_token we warned about so we don't re-toast across
  // a successful refresh (which mints a new token with a fresh exp).
  const warnedFor = useRef<string | null>(null);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;

    function check() {
      const token = getAccessToken();
      if (!token) return;
      const exp = decodeExp(token);
      if (exp == null) return;
      const now = Math.floor(Date.now() / 1000);
      const remaining = exp - now;
      if (remaining <= 0) return; // request() will handle via 401 → refresh
      if (remaining > WARN_SECONDS) return;
      if (warnedFor.current === token) return;
      warnedFor.current = token;
      const mins = Math.floor(remaining / 60);
      const secs = remaining % 60;
      const timeStr = `${mins}:${secs.toString().padStart(2, "0")}`;
      toast.warning(`Session expires in ${timeStr}`, {
        duration: Math.min(remaining * 1000, 120_000),
        description: "Click to stay signed in.",
        action: {
          label: "Stay signed in",
          onClick: async () => {
            const ok = await api.refreshSession();
            if (ok) {
              toast.success("Session extended");
              // The new access_token won't match warnedFor, so the next cycle
              // is eligible to warn again closer to its own expiry.
            } else {
              toast.error("Couldn't refresh. Please sign in again.");
            }
          },
        },
      });
    }

    check();
    timer = setInterval(check, POLL_MS);

    // Recompute on tab visibility change so backgrounded tabs don't miss the
    // warning window between polls.
    const onVisibility = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
}
